import { ResourceDetail } from '@cibo/core'
import { TraitFeature } from '@cibo/landmanager'
import { FormikWithWarningsProvider } from '@cibo/ui'
import { Button, Stack } from '@mui/material'
import { Form, FormikHelpers, FormikProps } from 'formik'
import { ReactNode, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

interface TemplateEditSingleFieldFormProps {
  traitId: string
  year: number
  requirement?: any
  currentValue?: ResourceDetail
  onSave: (filledValues: { value?: unknown; input?: unknown; result?: unknown }) => void
  requestClose: () => void
  DeleteButton?: ReactNode
}

export const TemplateEditSingleFieldForm = ({
  traitId,
  year,
  requirement,
  currentValue,
  onSave,
  requestClose,
  DeleteButton,
}: TemplateEditSingleFieldFormProps) => {
  const Trait = TraitFeature.forTraitId(traitId as any)
  const { t: traitT } = useTranslation(Trait?.ns)
  const { t } = useTranslation('@cibo/landmanager/DetailEditing')

  const DetailEditor = Trait?.simpleEditor ?? Trait?.editor

  // Minimal mock to pre-fill the form from the template's current draft value
  const mockFieldModels = useMemo(
    () => (currentValue ? [{ findDetail: () => currentValue } as any] : []),
    [currentValue]
  )

  const initialValues = useMemo(
    () =>
      ({
        input: Trait?.initialValues
          ? Trait.initialValues({ fieldModels: mockFieldModels, year })
          : {},
        traitId,
        source: (currentValue as any)?.source,
        year,
      } as ResourceDetail),
    [traitId, year, mockFieldModels]
  )

  const editorValidJsonSchema = useMemo(
    () =>
      Trait?.validJsonSchema
        ? {
            type: 'object' as const,
            properties: { input: Trait.validJsonSchema({ requirement, t: traitT }) },
          }
        : undefined,
    [Trait, requirement, traitT]
  )

  const handleSubmit = (
    { source: _source, ...values }: ResourceDetail,
    formikHelpers: FormikHelpers<any>
  ) => {
    const detailValue = Trait?.detailFromEditorValues
      ? Trait.detailFromEditorValues(values, requirement)
      : values
    const filledValues: { value?: unknown; input?: unknown; result?: unknown } = {}
    if (detailValue.input !== undefined) filledValues.input = detailValue.input
    if (detailValue.result !== undefined) filledValues.result = detailValue.result
    if (detailValue.value !== undefined) filledValues.value = detailValue.value
    onSave(filledValues)
    requestClose()
    formikHelpers.resetForm()
    formikHelpers.setSubmitting(false)
  }

  if (!DetailEditor) return null

  return (
    <FormikWithWarningsProvider<ResourceDetail>
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validJsonSchema={editorValidJsonSchema}
      enableReinitialize
    >
      {({ isValid, submitForm }: FormikProps<any>) => (
        <Form>
          <Stack>
            <Stack spacing={1} p={2} pb={0}>
              <DetailEditor
                traitId={traitId}
                name="input"
                fieldModels={mockFieldModels}
                // @ts-ignore year prop exists on byYear editors but isn't in the base type
                year={year}
                requirement={requirement}
              />
            </Stack>
            <Stack sx={{ background: 'background.paper' }} spacing={2} p={2} pt={0}>
              <Button
                style={{ width: '100%' }}
                variant="contained"
                onClick={submitForm}
                disabled={!isValid}
                data-testid="save-template-detail-button"
              >
                {currentValue ? t('savePractice') : t('addPractice')}
              </Button>
              {DeleteButton}
            </Stack>
          </Stack>
        </Form>
      )}
    </FormikWithWarningsProvider>
  )
}
